import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError, fetchCustomFields, putCustomFields } from '@/api/client'
import type { CustomFieldDefinition, CustomFieldType } from '@/api/types'

import { Sparkles } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

const FIELD_TYPES: CustomFieldType[] = ['text', 'pick_list', 'number', 'date']

type DraftField = CustomFieldDefinition & { optionsText?: string }

function emptyDraft(): DraftField {
  return { code: '', label: '', type: 'text', sort_order: 10, optionsText: '' }
}

export function CustomFieldsAdminPage() {
  const qc = useQueryClient()
  const fieldsQuery = useQuery({
    queryKey: ['config', 'custom-fields'],
    queryFn: async () => (await fetchCustomFields()).fields,
  })
  const [rows, setRows] = useState<DraftField[]>([])
  const [draft, setDraft] = useState<DraftField>(emptyDraft)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (fieldsQuery.data) {
      setRows(
        fieldsQuery.data.map((field) => ({
          ...field,
          optionsText: (field.options ?? []).join(', '),
        })),
      )
    }
  }, [fieldsQuery.data])

  const saveMut = useMutation({
    mutationFn: (fields: CustomFieldDefinition[]) => putCustomFields(fields),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'custom-fields'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to save custom fields')
    },
  })

  function toPayload(list: DraftField[]): CustomFieldDefinition[] {
    return list.map(({ code, label, type, sort_order, optionsText }) => {
      const base: CustomFieldDefinition = {
        code: code.trim(),
        label: label.trim(),
        type,
        sort_order,
      }
      if (type === 'pick_list') {
        base.options = (optionsText ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      }
      return base
    })
  }

  function addField(event: FormEvent) {
    event.preventDefault()
    if (!draft.code.trim() || !draft.label.trim()) {
      setError('Code and label are required')
      return
    }
    setRows((prev) => [...prev, { ...draft }])
    setDraft(emptyDraft())
    setError(null)
  }

  return (
    <AdminSection
      title="Custom fields"
      tone="indigo"
      icon={Sparkles}
      lede="Define extra opportunity fields (text, pick list, number, or date). Values are stored on each opportunity and shown on the edit form."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="table-wrap">
        <table className="table table--dense">
          <thead>
            <tr>
              <th>Label</th>
              <th>Code</th>
              <th>Type</th>
              <th>Options</th>
              <th>Sort</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.code}-${index}`}>
                <td>
                  <input
                    className="field__input"
                    value={row.label}
                    onChange={(e) =>
                      setRows((prev) =>
                        prev.map((r, i) => (i === index ? { ...r, label: e.target.value } : r)),
                      )
                    }
                  />
                </td>
                <td>
                  <input
                    className="field__input"
                    value={row.code}
                    onChange={(e) =>
                      setRows((prev) =>
                        prev.map((r, i) => (i === index ? { ...r, code: e.target.value } : r)),
                      )
                    }
                  />
                </td>
                <td>
                  <select
                    className="field__input"
                    value={row.type}
                    onChange={(e) =>
                      setRows((prev) =>
                        prev.map((r, i) =>
                          i === index ? { ...r, type: e.target.value as CustomFieldType } : r,
                        ),
                      )
                    }
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {row.type === 'pick_list' ? (
                    <input
                      className="field__input"
                      placeholder="AMER, EMEA"
                      value={row.optionsText ?? ''}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((r, i) =>
                            i === index ? { ...r, optionsText: e.target.value } : r,
                          ),
                        )
                      }
                    />
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  <input
                    className="field__input"
                    type="number"
                    value={row.sort_order}
                    onChange={(e) =>
                      setRows((prev) =>
                        prev.map((r, i) =>
                          i === index ? { ...r, sort_order: Number(e.target.value) } : r,
                        ),
                      )
                    }
                  />
                </td>
              </tr>
            ))}
            {rows.length === 0 && !fieldsQuery.isLoading && (
              <tr>
                <td colSpan={5} className="empty">
                  No custom fields yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="admin-form__actions">
        <button
          className="button button--primary"
          type="button"
          disabled={saveMut.isPending}
          onClick={() => saveMut.mutate(toPayload(rows))}
        >
          Save catalogue
        </button>
      </div>

      <form className="admin-form" onSubmit={addField}>
        <h3 className="admin-form__title">Add field</h3>
        <div className="admin-form__row">
          <label className="field">
            <span className="field__label">Code</span>
            <input
              className="field__input"
              value={draft.code}
              onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Label</span>
            <input
              className="field__input"
              value={draft.label}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Type</span>
            <select
              className="field__input"
              value={draft.type}
              onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value as CustomFieldType }))}
            >
              {FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          {draft.type === 'pick_list' && (
            <label className="field">
              <span className="field__label">Options (comma-separated)</span>
              <input
                className="field__input"
                value={draft.optionsText ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, optionsText: e.target.value }))}
              />
            </label>
          )}
          <label className="field">
            <span className="field__label">Sort</span>
            <input
              className="field__input"
              type="number"
              value={draft.sort_order}
              onChange={(e) => setDraft((d) => ({ ...d, sort_order: Number(e.target.value) }))}
            />
          </label>
          <button className="button button--secondary" type="submit">
            Add to list
          </button>
        </div>
      </form>
    </AdminSection>
  )
}
